import React, { useRef, useState, useEffect } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgMoreButton from '@components/ringil3/icons/buttons/MoreButton';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { Details, Summary, Menu, SectionTitle, MenuList, MenuItem } from './components/MoreButtonComponents';

export interface MenuItem {
  id: string;
  label: string;
  icon?: React.ReactElement;
  onClick: () => void;
  visible?: boolean;
  disabled?: boolean;
  className?: string;
}

export interface MenuSection {
  id: string;
  title: string;
  titleKey?: string; // i18n key for the title
  visible?: boolean;
  items: MenuItem[];
}

export interface RingilMoreButtonsProps {
  // Visual customization
  buttonColor?: string;
  buttonIcon?: React.ReactNode;
  buttonTitle?: string;

  // Menu structure
  sections: MenuSection[];

  // Behavior
  closeOnItemClick?: boolean;
  disabled?: boolean;

  // Styling
  menuPosition?: 'right' | 'left';
  menuWidth?: string;
  className?: string;

  // Events
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  onItemClick?: (itemId: string) => void;
}

const RingilMoreButtons: React.FC<RingilMoreButtonsProps> = ({
  buttonColor = '#616061',
  buttonIcon,
  buttonTitle,
  sections,
  closeOnItemClick = true,
  disabled = false,
  menuPosition = 'right',
  menuWidth = '200px',
  className = '',
  onMenuOpen,
  onMenuClose,
  onItemClick,
}) => {
  const { tr } = useTranslationLgs();
  const [open, setOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (detailsRef.current && !detailsRef.current.contains(event.target as Node)) {
        if (open) {
          setOpen(false);
          onMenuClose?.();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onMenuClose]);


  // Handle menu toggle
  const handleToggle = (e: React.MouseEvent) => {
    if (disabled) return;

    if (open) {
      return;
    }

    const newOpen = !open;
    setOpen(newOpen);
    e.preventDefault();

    if (newOpen) {
      onMenuOpen?.();
    } else {
      onMenuClose?.();
    }
  };

  // Handle item click
  const handleItemClick = (item: MenuItem) => {
    if (item.disabled) return;

    if (closeOnItemClick) {
      setOpen(false);
      onMenuClose?.();
    }

    onItemClick?.(item.id);
    item.onClick();
  };

  // Filter visible sections and items
  const visibleSections = sections
    .filter(section => section.visible !== false)
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.visible !== false)
    }))
    .filter(section => section.items.length > 0);

  if (visibleSections.length === 0) {
    return null;
  }

  // Convert common widths to Tailwind classes
  const getWidthClasses = (width: string) => {
    const widthMap: Record<string, string> = {
      '150px': 'min-w-[150px]',
      '200px': 'min-w-[200px]',
      '250px': 'min-w-[250px]',
      '300px': 'min-w-[300px]',
    };
    return widthMap[width] || '';
  };

  const widthClass = getWidthClasses(menuWidth);

  return (
    <div className={`relative inline-block ${className}`}>
      <Details
        ref={detailsRef}
        onClick={handleToggle}
        open={open}
      >
        <Summary disabled={disabled} title={buttonTitle}>
          {buttonIcon || <SvgMoreButton fill={buttonColor} />}
        </Summary>

        {open && (
          <Menu
            position={menuPosition}
            className={`max-w-[min(300px,calc(100vw-20px))] ${widthClass}`}
            {...(!widthClass && { style: { minWidth: menuWidth } })}
          >
            {visibleSections.map((section) => (
              <div key={section.id}>
                <SectionTitle>
                  {section.titleKey ? tr(section.titleKey, section.title) : section.title}
                </SectionTitle>

                <MenuList>
                  {section.items.map((item) => (
                    <MenuItem key={item.id} disabled={item.disabled}>
                      <RingilButton
                        mode="text-only"
                        icon={item.icon}
                        label={item.label}
                        disabled={item.disabled}
                        className={`justify-start text-left w-full ${item.className || ''}`}
                        onClick={() => handleItemClick(item)}
                      />
                    </MenuItem>
                  ))}
                </MenuList>
              </div>
            ))}
          </Menu>
        )}
      </Details>
    </div>
  );
};

export default RingilMoreButtons;
